import { ContentType } from '@creditsights/api-ui-web';
import { AccessStatus } from '../services/dtos/article/article.dto';
import { Tag } from './Tag';

export interface Component {
  body?: string | null;
  tags: Tag[];
  title: string | null;
  file?: { url: string; pdfContent?: boolean } | null;
}

export interface ArticleContent {
  title: string;
  summary: string;
  components: Component[];
  includedContent?: ArticleIncludedContent[];
  isPrivate: boolean;
}

export interface ArticleInnerContent {
  title: string;
  summary: string;
  isPrivate: boolean;
}

export interface ArticleIncludedContent {
  id: number;
  modifiedDate: string;
  publishDate: string;
  canAccess: boolean;
  content: ArticleInnerContent;
  tags: Tag[];
  articleType?: ContentType;
  companies: Tag[];
}

export interface Quota {
  usageIncreased: boolean;
  usageExceeded: boolean;
  counter: number;
  limit: number;
}

export interface Article {
  id: number;
  content: ArticleContent;
  tags: Tag[];
  focusTags: Tag[];
  modifiedDate: string;
  publishDate: string;
  articleType?: ContentType;
  companies: Tag[];
  wordCount: number;
  imageCount: number;
  analysts: string[];
  canAccess: boolean;
  quota: Quota | null;
  accessStatus: AccessStatus;
}

export enum Region {
  UnitedStates = 'us',
  Europe = 'euro',
}

export enum FeedType {
  My,
  Global,
  SpecialSituations,
}

export enum PageType {
  RecentlyPublished,
  Search,
}

export enum ContentTypeTag {
  Research = 'type-tag/research',
  Covenants = 'type-tag/covenants',
  News = 'type-tag/news',
}

export enum CovenantTypeTag {
  BondTransaction = 'type-tag/bond_report',
  LoanTransaction = 'type-tag/loan_report',
  EventDrivenTopicalResearch = 'type-tag/event_driven_report',
}

export enum NewsTypeTag {
  LFIDailyInsights = 'type-tag/lfi_daily_insights',
  StructuredFinanceDailyWrap = 'type-tag/structured_finance_daily_wrap',
  CLOWeekly = 'type-tag/clo_weekly',
  HighYieldBondWeekly = 'type-tag/high_yield_bond_weekly',
  LeveragedLoanWeekly = 'type-tag/leveraged_loan_weekly',
  LFIWeekly = 'type-tag/lfi_weekly',
  MAWeekly = 'type-tag/ma_activity_weekly',
  PrivateCreditWeekly = 'type-tag/private_credit_weekly',
  SpecialSituationsWeekly = 'type-tag/special_situations_weekly',
  StructuredFinanceWeekly = 'type-tag/structured_finance_weekly',
  CLOMonthly = 'type-tag/clo_monthly',
  HighYieldMonthly = 'type-tag/high_yield_monthly',
  LeveragedLoanMonthly = 'type-tag/leveraged_loan_monthly',
  MAMonthly = 'type-tag/ma_activity_monthly',
  SecondaryMarketMonthly = 'type-tag/secondary_market_monthly',
  SpecialSituationsMonthly = 'type-tag/special_situations_monthly',
  HighYieldQuarterly = 'type-tag/high_yield_quarterly',
  LFIQuarterly = 'type-tag/lfi_quarterly',
  CLOInsights = 'type-tag/clo_insights',
  HighYieldBondInsights = 'type-tag/high_yield_bond_insights',
  LeveragedLoanInsights = 'type-tag/leveraged_loan_insights',
  LFIDataInsights = 'type-tag/lfi_data_insights',
  LFIInsights = 'type-tag/lfi_insights',
  MAInsights = 'type-tag/m_and_a_insights',
  NewIssueInsights = 'type-tag/new_issue_insights',
  PrivateCreditInsights = 'type-tag/private_credit_insights',
  SecondaryMarketInsights = 'type-tag/secondary_market_insights',
  SpecialSituationsInsights = 'type-tag/special_situations_insights',
  StructuredFinanceInsights = 'type-tag/structured_finance_insights',
  EventsCalendar = 'type-tag/events_calendar',
}

export const COVENANT_TYPE_TAG_LIST: string[] = Object.values(CovenantTypeTag);

export const NEWS_TYPE_TAG_LIST: string[] = Object.values(NewsTypeTag);

export const REGIONS = {
  NorthAmerica: 'region/21',
  Europe: 'region/150',
};

export interface ArticlePermissions {
  canAccess: boolean;
  accessStatus: AccessStatus;
  tags: Tag[];
  content: {
    isPrivate: boolean;
  };
  quota: Quota | null;
}

export interface FeedArticlePermissions {
  canAccess: boolean;
  content: {
    isPrivate: boolean;
  };
}

export enum ArticleLockType {
  Subscribe = 'Subscribe',
  RequestAccess = 'Request Access',
}
